/**
 * Post data lives in plain JS so there is no markdown dependency and no
 * database. To publish a new article: add a file like this one, register it
 * in ./index.js, drop its images in /public/blog-images/, and push.
 *
 * Supported blocks:
 *   { type: "p",       text }                    inline: **bold**, `code`, [label](url)
 *   { type: "h2",      text }                    (feeds the "On this page" list)
 *   { type: "h3",      text }
 *   { type: "ul",      items: [text] }
 *   { type: "ol",      items: [text] }
 *   { type: "callout", tone: "mint|lilac|peach", title, text }
 *   { type: "quote",   text }
 *   { type: "figure",  src, alt, caption }
 *   { type: "table",   headers: [..], rows: [[..], [..]] }   (cells accept inline markup)
 *   { type: "chat",    title, caption, muted?, shop, messages: [{ from: "buyer|shop|time", text }] }
 */
const post = {
  slug: "sell-on-instagram-dms-without-losing-orders",
  title: "How to Sell on Instagram Without Losing Orders in Your DMs",
  seoTitle: "Sell on Instagram Without Losing DM Orders",
  description:
    "Most Instagram sellers lose orders in the back-and-forth of DMs. Here are the five places sales slip away, and how a DM checkout fixes each one.",
  excerpt:
    "A customer asks \u201cis this available?\u201d and three messages later the sale is gone. Here is where DM orders leak, and how to build a checkout that doesn\u2019t.",
  category: "Selling on Instagram",
  tags: ["Instagram selling", "DM checkout", "Instagram DM automation"],
  date: "2026-10-07",
  cover: {
    src: "/blog-images/sell-on-instagram-dms-cover.png",
    alt: "A phone showing an Instagram DM conversation that turns into a paid order",
  },
  blocks: [
    {
      type: "p",
      text:
        "If you sell on Instagram, you already know how most orders start. Someone sees a post, opens your DMs and types a few words: \u201cis this still available?\u201d",
    },
    {
      type: "p",
      text:
        "What happens next decides whether you make the sale. For most small sellers, it looks something like this.",
    },
    {
      type: "chat",
      muted: true,
      shop: "yourshop",
      title: "The manual way",
      caption: "Illustrative example of a typical manual DM sale.",
      messages: [
        { from: "buyer", text: "Hi, is the linen kurta set available?" },
        { from: "time", text: "4 hours later" },
        { from: "shop", text: "Yes! M and L left. \u20B91,899" },
        { from: "buyer", text: "M please. How do I pay?" },
        { from: "shop", text: "UPI to the number in my bio. Send a screenshot and your address." },
        { from: "time", text: "Next day" },
        { from: "buyer", text: "Sorry, I was travelling. Is it still available?" },
      ],
    },
    {
      type: "p",
      text:
        "Nobody did anything wrong here. The seller was busy and the buyer was travelling. But the sale still stalled, and neither of them could point to the moment it was lost. That is what makes DM selling so frustrating: the leaks are small, they are everywhere, and they are invisible.",
    },

    { type: "h2", text: "The five places DM orders slip away" },
    {
      type: "p",
      text:
        "Look back at your last ten abandoned conversations and you will probably find the same few causes.",
    },
    {
      type: "figure",
      src: "/blog-images/sell-on-instagram-dms-leaks.png",
      alt: "Diagram of the five stages of a DM order: enquiry, reply, payment, address and delivery, each with the problem that usually breaks it",
      caption: "The five places a DM order usually breaks.",
      width: 1200,
      height: 1000,
    },
    {
      type: "ol",
      items: [
        "**Slow or missed replies.** DMs arrive at night, during festivals and while you are packing parcels. A buyer who waits half a day often moves on to the next shop in their feed.",
        "**The same questions, again and again.** Price, size, colour, availability, delivery time. You type the same answers dozens of times a week, and each one is a chance for a typo or a forgotten detail.",
        "**Payment friction.** Sharing payment details, asking for a screenshot, matching it to the right customer and checking the amount. Every step asks the buyer to do more work than a normal checkout would.",
        "**Addresses in free text.** An address typed across five messages, missing a postal code or a phone number, turns into a delivery problem later, and sometimes a refund problem after that.",
        "**No clear record.** Without an order list it is hard to know who paid, who did not, what needs packing and who is waiting for an update. Your orders end up living in a chat scroll.",
      ],
    },

    { type: "h2", text: "What a good DM checkout looks like" },
    {
      type: "p",
      text:
        "The fix is not necessarily a website (see our guide to [selling on Instagram without a website](/blog/sell-on-instagram-without-a-website)). Your customers are already inside Instagram, and sending them somewhere else adds one more place for people to drop off. A better approach is to let the conversation do the checkout, so the buyer never leaves the chat.",
    },
    { type: "p", text: "A good flow follows the same steps every time:" },
    {
      type: "ol",
      items: [
        "**Start.** The buyer begins with a keyword in your DMs, a comment on a post, or a reply to your Story with a keyword.",
        "**Browse.** They see your products as a catalog inside the thread, with prices. A Story reply skips this step and sends the product card straight away.",
        "**Choose.** They pick a product, then a size or variant if it has one, and add it to their cart.",
        "**Deliver.** They share their name, phone number, email and delivery address one detail at a time instead of typing a paragraph. If you have activated coupons on the Growth or Pro plan, they can enter a code here too.",
        "**Pay.** They pay in the same chat, either with a secure payment link, where any shipping fee you have set is added to the total, or by sending payment to your UPI or bank details.",
        "**Confirm.** The order lands in your dashboard, and with a connected payment gateway it is confirmed automatically the moment the payment arrives.",
      ],
    },
    {
      type: "chat",
      shop: "yourshop",
      title: "With a DM checkout",
      caption: "Illustrative example of an automated DM checkout. Your own flow will use your products and wording.",
      messages: [
        { from: "buyer", text: "kurta" },
        { from: "shop", text: "Hi! Here is what is in stock. Tap a product to see sizes." },
        { from: "shop", text: "Linen Kurta Set \u2022 \u20B91,899\nSizes: S, M, L" },
        { from: "buyer", text: "M" },
        { from: "shop", text: "Added 1 \u00D7 Linen Kurta Set (M) to your cart. Where should we deliver it?" },
        { from: "time", text: "Two minutes later" },
        { from: "shop", text: "Here is your secure payment link. Your order is confirmed as soon as it is paid." },
      ],
    },
    {
      type: "callout",
      tone: "mint",
      title: "Why this works",
      text:
        "Every step is one small action. The buyer is never asked to copy a number, take a screenshot or write a paragraph, and fewer decisions means fewer places to give up.",
    },

    { type: "h2", text: "Three ways customers can start a purchase" },
    {
      type: "p",
      text:
        "You do not have to wait for people to message you first. The same checkout can begin in three places:",
    },
    {
      type: "ul",
      items: [
        "**A keyword in your DMs.** Someone sends a word like a product name or category and the catalog opens.",
        "**A comment on a post or Reel.** The buyer comments, and the flow starts in their DMs without you copying and pasting a link.",
        "**A reply to your Story.** The buyer replies with a keyword you chose, such as RED or LINK, and the matching product card arrives in their DMs. It is one of the warmest signals you get, because the buyer is already looking at the product. Growth and Pro match each keyword to its own product, and other plans send a general reply. Read more in [Story replies that sell](/blog/instagram-story-replies-that-sell).",
      ],
    },
    {
      type: "p",
      text:
        "Comment-to-DM automation is available from the Creator plan onward. See the [pricing page](/pricing) for what each plan includes.",
    },

    { type: "h2", text: "Payments: keep it simple, keep it yours" },
    {
      type: "p",
      text:
        "You have two options. You can accept payments manually through UPI, a QR code or a bank transfer, which needs no payment gateway at all, but you confirm each payment yourself from your dashboard. Or you can connect Razorpay, Stripe or PayPal so payments are confirmed automatically.",
    },
    {
      type: "p",
      text:
        "Either way, the money goes straight to your own account. Checkout DM does not take a commission or a transaction fee on your orders. If you use a payment gateway, the gateway\u2019s own charges still apply.",
    },

    { type: "h2", text: "Do not automate away the human part" },
    {
      type: "p",
      text:
        "Automation should handle the repetitive questions so you have time for the conversations that matter: a custom request, a worried first-time buyer, a question no menu can answer. A good setup lets a customer ask for a real person at any point, and lets you step in without breaking the flow.",
    },
    {
      type: "quote",
      text: "Automate the checkout, not the relationship.",
    },

    { type: "h2", text: "A simple setup checklist for this week" },
    {
      type: "ol",
      items: [
        "Pick your best-selling 5 to 10 products and write clear names, prices and sizes for each.",
        "Take one good photo per product. Square, bright and uncluttered beats fancy.",
        "Decide how you will take payments: manual UPI, or a connected gateway for automatic confirmation.",
        "Write down your delivery time and return policy in two sentences each, so they are ready to share.",
        "Run through the whole flow yourself from a second Instagram account before you tell customers about it.",
      ],
    },
    {
      type: "p",
      text:
        "Checkout DM has a free plan that includes 5 products and 15 customer conversations a month, which is enough to run this checklist and see how the flow feels. You can read how the dashboard works in the [Help Guide](/help), or [connect your Instagram account](/login) and try it with your own catalog.",
    },
  ],
  related: [
    { to: "/help", title: "Help Guide", description: "Every Checkout DM guide in one place: catalog, payments, shipping and more." },
    { to: "/catalog-guide", title: "Catalog Guide", description: "Product cards, stock, sizes and how in-DM product search works." },
  ],
  faqs: [
    {
      q: "Can I sell on Instagram without a website?",
      a: "Yes. You can run the whole sale inside Instagram DMs: the buyer browses your catalog, chooses a product, shares a delivery address and pays, all in the same chat. A DM checkout tool such as Checkout DM automates those steps so you do not have to type each reply yourself.",
    },
    {
      q: "How do I take payments from Instagram customers?",
      a: "You can share UPI, a QR code or bank details and confirm each payment manually, or connect a payment gateway like Razorpay, Stripe or PayPal so payments are confirmed automatically. With Checkout DM, payments go directly to your own account.",
    },
    {
      q: "Does Checkout DM take a commission on sales?",
      a: "No. Checkout DM does not take a commission or a transaction fee on your orders. If you use a payment gateway, the gateway\u2019s own charges apply.",
    },
  ],
};

export default post;
